import { Component, inject, OnInit, signal, viewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IonBackButton, IonButtons, IonContent, IonHeader, IonMenuButton, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { lastValueFrom } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  WorkPermissionsEnum,
  WorkPersonnelFileEntry,
  WorkPersonnelFilesV2Service,
} from '@work-frontend-monorepo/work-rest-api';
import { ButtonComponent } from '@work-frontend-monorepo/work-ui/layout';
import { ExceptionHandlingService } from '@work-frontend-monorepo/work-ui/services';
import { ProfileState } from '@work-frontend-monorepo/work-ui/state-management';
import { PersonaldossierCardComponent } from '../../components/personaldossier-card/personaldossier-card.component';
import { PersonaldossierCreateUpdateModalComponent } from '../../components/personaldossier-create-update-modal/personaldossier-create-update-modal.component';

@Component({
  selector: 'sfg-app-adressbuch-mitarbeitende-personaldossier-page',
  templateUrl: './adressbuch-mitarbeitende-personaldossier-page.component.html',
  styleUrls: ['./adressbuch-mitarbeitende-personaldossier-page.component.scss'],
  standalone: true,
  imports: [
    IonContent,
    IonHeader,
    IonTitle,
    IonToolbar,
    IonBackButton,
    IonMenuButton,
    IonButtons,
    TranslatePipe,
    ButtonComponent,
    PersonaldossierCardComponent,
    PersonaldossierCreateUpdateModalComponent,
  ],
})
export class AdressbuchMitarbeitendePersonaldossierPageComponent implements OnInit {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly personnelFilesService = inject(WorkPersonnelFilesV2Service);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly store = inject(Store);

  modal = viewChild<PersonaldossierCreateUpdateModalComponent>('modal');

  userId = signal<number | undefined>(undefined);
  entries = signal<WorkPersonnelFileEntry[]>([]);

  isAdmin = toSignal(this.store.select(ProfileState.hasPermission(WorkPermissionsEnum.PersonnelFileADMIN)));
  canCreate = toSignal(this.store.select(ProfileState.hasPermission(WorkPermissionsEnum.PersonnelFileCREATE)));
  canUpdate = toSignal(this.store.select(ProfileState.hasPermission(WorkPermissionsEnum.PersonnelFileUPDATE)));
  canDelete = toSignal(this.store.select(ProfileState.hasPermission(WorkPermissionsEnum.PersonnelFileDELETE)));

  async ngOnInit() {
    const userId = this.activatedRoute.snapshot.params['userId'];
    if (userId) {
      this.userId.set(Number(userId));
      await this.loadEntries();
    }
  }

  async loadEntries() {
    try {
      const userId = this.userId();
      if (!userId) return;
      const result = await lastValueFrom(
        this.personnelFilesService.personnelFilesV2ControllerFindByUserId({ userId }),
      );
      this.entries.set(result ?? []);
    } catch (loadEntriesException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadPersonaldossierException', loadEntriesException);
    }
  }

  protected openCreateModal() {
    this.modal()?.openModal();
  }

  protected openEditModal(entry: WorkPersonnelFileEntry) {
    this.modal()?.openModal(entry);
  }

  protected async deleteEntry(entry: WorkPersonnelFileEntry) {
    try {
      await lastValueFrom(this.personnelFilesService.personnelFilesV2ControllerSoftDelete({ id: entry.id }));
      await this.loadEntries();
    } catch (deleteEntryException) {
      this.exceptionHandlingService.handleExceptionWithAlert('deletePersonaldossierEntryException', deleteEntryException);
    }
  }
}
